import {
  Auth0AccountRole,
  MessageTemplatesService,
  TemplatePreview,
  TemplateRecipient,
  TemplateSendResult,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import Notifications from "util/notifications";

const BATCH = 20;

/**
 * A template's wording: the email's subject and body (or its own HTML,
 * when email_format is "html") and the text. A field left out is the
 * template's own.
 */
export type TemplateWording = {
  email_subject?: string;
  email_body?: string;
  email_format?: "editor" | "html";
  email_html?: string;
  sms_body?: string;
};

/**
 * Send a template to people a few at a time, so a long list never holds
 * one request open for long. Each person gets it filled in for them.
 * Wording given is for this send only. A parent chosen with their student
 * gets only their own message, even when the two are in different batches.
 */
export async function sendTemplateInBatches({
  templateId,
  recipients,
  sms,
  email,
  wording,
}: {
  templateId: number;
  recipients: TemplateRecipient[];
  sms: boolean;
  email: boolean;
  wording?: TemplateWording;
}): Promise<TemplateSendResult[]> {
  const results: TemplateSendResult[] = [];
  const parentsChosen = recipients
    .filter((r) => r.person_type === Auth0AccountRole.PARENT)
    .map((r) => r.person_id);
  for (let i = 0; i < recipients.length; i += BATCH) {
    try {
      const response = await MessageTemplatesService.sendMessageTemplate({
        templateId,
        requestBody: {
          ...wording,
          recipients: recipients.slice(i, i + BATCH),
          sms,
          email,
          parents_chosen: parentsChosen,
        },
      });
      results.push(...response.results);
    } catch (e) {
      if (results.length === 0) throw e; // nothing went out yet
      // Earlier batches went out: report them, and everyone after as not sent.
      const reason =
        "The send stopped early. Look in Communication before sending again.";
      results.push(
        ...recipients.slice(i).map((r) => ({
          ...r,
          name: "",
          status: TemplateSendResult.status.FAILED,
          reason,
        }))
      );
      break;
    }
  }
  return results;
}

/** One plain sentence about how a send went. */
export function sendSummary(results: TemplateSendResult[]): string {
  const sent = results.filter((r) => r.status === "sent");
  const notSent = results.filter((r) => r.status !== "sent");
  if (notSent.length === 0) {
    return sent.length === 1
      ? `Message sent to ${sent[0].name}.`
      : `Message sent to ${sent.length} people.`;
  }
  const reasons = notSent
    .slice(0, 3)
    .map((r) => `${r.name || "Someone"}: ${r.reason}`)
    .join(" ");
  const more = notSent.length > 3 ? ` And ${notSent.length - 3} more.` : "";
  return `Sent to ${sent.length}, not sent to ${notSent.length}. ${reasons}${more}`;
}

/** After someone is added in an invite dialog: send them the chosen template, if any. */
export async function sendChosenTemplate({
  templateId,
  sms,
  email,
  personType,
  personId,
}: {
  templateId: number | null;
  sms: boolean;
  email: boolean;
  personType: TemplateRecipient["person_type"];
  personId?: number | null;
}): Promise<boolean> {
  if (templateId === null || !personId) return false;
  if (!sms && !email) {
    Notifications.error("The message was not sent: pick SMS, email, or both.");
    return false;
  }
  try {
    const results = await sendTemplateInBatches({
      templateId,
      sms,
      email,
      recipients: [{ person_type: personType, person_id: personId }],
    });
    const summary = sendSummary(results);
    const allSent = results.every((r) => r.status === "sent");
    if (allSent) Notifications.success(summary);
    else Notifications.error(summary);
    return allSent;
  } catch (e) {
    Notifications.error(errorText(e, "The message could not be sent."));
    return false;
  }
}

/** "1 text" or "3 texts, Unicode": how a text is sent and billed. */
export function segmentWords(preview: TemplatePreview): string {
  const texts =
    preview.sms_segments === 1 ? "1 text" : `${preview.sms_segments} texts`;
  return `${preview.sms_characters} characters, ${texts}`;
}
